import {
  alpha,
  Avatar,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Dispatch, SetStateAction } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { getFeaturePermission } from '@zeta-commons';
import {
  convetToTitleCase,
  useProfileProgressStore,
  useUserAccessStore,
  useUserDetailsStore,
} from '@zeta-core';
import { ChevronLeftSvgIcon, WarningNavIcon } from '@zeta-core/assets';
import { IClientCardItem, IFactFindEntity } from '@zeta-types';
import { RouterLinks } from '../../app-route-paths';
import { Users } from '../../auth/route-constants';
import { DASHBOARD_CONFIG, getFirmProfileNavigation, MY_PROFILE_NAVIGATION } from '../common/constants';
import { canAccessMeetingConfiguration } from '../../pages/dashboard/profile-setup/user-management/constant';
import { checkWarningStatus } from './helper';

interface Props {
  stretch: boolean;
  setStretch: Dispatch<SetStateAction<boolean>>;
  disableNavOptions?: boolean;
}

interface NavItems {
  title: string;
  icon: () => JSX.Element;
  description: string;
  path: string;
  warning?: boolean;
  warningInfo?: string;
  isEnabled?: boolean;
}

interface ProfileNodesProps {
  navItems: NavItems[];
}

const ProfileSetupNavigation = ({ stretch, setStretch, disableNavOptions }: Props) => {
  // Legacy subscription-plan-based navigation path kept commented for reference.
  // const currentSubscription = useSubscriptionDetailsStore((state) => state.subscription);
  // const subscriptionPlans = useSubscriptionPlansStore((state) => state.subscriptionPlans);
  // const currentSubscriptionPlan = subscriptionPlans.find((plan) => plan.planId === currentSubscription?.plans?.[0]?.licenceReferenceID) || {
  //   planFeatures: [],
  // };
  // const accessibleFeatures = currentSubscriptionPlan?.planFeatures;

  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const locationState = (state || {}) as { entityData: IFactFindEntity; clientData: IClientCardItem };
  const progressData = useProfileProgressStore((state) => state.progressData);
  const { firstName, lastName, role, orgRole } = useUserDetailsStore((state) => state.user);
  const userAccess = useUserAccessStore((state) => state.userAccess);
  const canSeeMeetingConfiguration = canAccessMeetingConfiguration(orgRole);

  const getUserRole = () => {
    if (orgRole?.length) {
      const userRoles = orgRole.map((e: string) => {
        return convetToTitleCase(e);
      });

      let role = '';
      if (userRoles.length === 1) role = userRoles[0];
      else if (userRoles.length === 2) role = `${userRoles[0]} & ${userRoles[1]}`;
      else if (userRoles.length > 2) {
        const lastItem = userRoles.at(-1);
        role = userRoles.slice(0, -1).join(' ,').concat(` & ${lastItem}`);
      }

      return (
        <Typography variant="chipLabel" color={'primary.main'}>
          {role}
        </Typography>
      );
    }

    return (
      <Typography variant="chipLabel" color={'text.disabled'}>
        Define user role from User Management
      </Typography>
    );
  };

  const ProfileNodes = ({ navItems }: ProfileNodesProps) => {
    return navItems.map((item) => {
      const showWarning = role === Users.ADMIN && checkWarningStatus(item, progressData);
      const hidePath = (item.path === RouterLinks.meetingConfiguration || item.path === RouterLinks.certificationsAndExpertise) && !canSeeMeetingConfiguration;
      const isActive = pathname.includes(item.path);

      return (
        <>
          {!hidePath && (
            <>
              {item.isEnabled && (
                <Tooltip title={item.title} key={item.title} disableHoverListener={stretch} placement="right">
                  <ListItem disableGutters disablePadding>
                    <ListItemButton
                    disabled={item.path === RouterLinks.billingAndInvoice ? false : disableNavOptions}
                    onClick={() => navigate(item.path, { state: locationState })}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      minHeight: 46,
                      bgcolor: isActive ? alpha(theme.palette.primary.main, 0.16) : 'inherit',
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                      '& .MuiTouchRipple-child': { bgcolor: `${theme.palette.primary.main} !important` },
                      '&.Mui-disabled': {
                        opacity: 0.38,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: theme.palette.primary.main, minWidth: stretch ? 40 : 0, mr: stretch ? 0 : 0 }}>
                      <item.icon />
                    </ListItemIcon>
                    {stretch && (
                      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            fontWeight: isActive ? 600 : 500,
                            color: 'text.primary',
                            id: item.title,
                          }}
                        />
                        {showWarning && <WarningNavIcon />}
                      </Stack>
                    )}
                  </ListItemButton>
                </ListItem>
                </Tooltip>
              )}
            </>
          )}
        </>
      );
    });
  };

  return (
    <Stack
      sx={{
        maxWidth: stretch ? DASHBOARD_CONFIG.NAVIGATION.DESKTOP_WIDTH : '92px',
        width: '100%',
        position: 'fixed',
        height: `calc(100vh - ${DASHBOARD_CONFIG.HEADER.DESKTOP_HEIGHT}px)`,
        left: 0,
        top: DASHBOARD_CONFIG.HEADER.DESKTOP_HEIGHT,
        zIndex: theme.zIndex.appBar + 3,
        bgcolor: 'background.paper',
        borderRight: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack sx={{ p: 3, px: 3, overflowY: 'auto', height: '100%', bgcolor: 'background.paper' }}>
        {stretch ? (
          <Stack direction="column" sx={{ gap: 1, p: 2, border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`, borderRadius: 1, mb: 2 }}>
            <Stack direction="row" alignItems="center" gap={1.25}>
              <Avatar
                sizes="23"
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                }}
              >
                {firstName?.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6">
                  {firstName} {lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {role === Users.ADMIN ? 'Super Admin' : role}
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ borderStyle: 'dashed', my: 1, borderColor: alpha(theme.palette.grey[500], 0.8) }} />

            <Stack spacing={0.75}>
              <Chip
                label={role === Users.ADMIN ? 'Super Admin' : role}
                size="small"
                variant="soft"
                sx={{ width: 'fit-content' }}
              />
              {getUserRole()}
            </Stack>
          </Stack>
        ) : (
          <Stack alignItems="center" mb={2}>
            <Avatar
              sizes="23"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
              }}
            >
              {firstName?.charAt(0).toUpperCase()}
            </Avatar>
          </Stack>
        )}

        {getFeaturePermission(userAccess?.access, 'AdvisorOrgConfig').accessible && (
          <>
            {stretch && (
              <Typography p="10px" variant="overline" color="text.secondary">
                Firm Profile
              </Typography>
            )}
            <List disablePadding>{<ProfileNodes navItems={getFirmProfileNavigation(userAccess)} />}</List>
            <Divider sx={{ borderStyle: 'dashed', my: 2, borderColor: alpha(theme.palette.grey[500], 0.8) }} />
          </>
        )}

        {stretch && (
          <Typography p="10px" variant="overline" color="text.secondary">
            My Profile
          </Typography>
        )}
        <List disablePadding>{<ProfileNodes navItems={MY_PROFILE_NAVIGATION} />}</List>

        <Box
          onClick={() => setStretch((x) => !x)}
          sx={{
            mt: 2,
            ml: stretch ? 'auto' : 0,
            width: 34,
            height: 34,
            borderRadius: 999,
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
            color: theme.palette.text.primary,
            border: `1px solid ${alpha(theme.palette.grey[500], 0.24)}`,
          }}
        >
          <SvgIcon
            sx={{
              rotate: stretch ? '0deg' : '180deg',
            }}
          >
            <ChevronLeftSvgIcon />
          </SvgIcon>
        </Box>
      </Stack>
    </Stack>
  );
};

export default ProfileSetupNavigation;
